// "Perfil del negocio" — the dedicated surface where the owner fills in their
// business essentials in plain language. Two kinds of info:
//   • identity (name / activity / agent name / treatment) → composes WHO the
//     agent is, and
//   • the FACTS the agent should always know (address, hours, phone, services,
//     how to book, + vertical-specific fields) — these go ALWAYS into the
//     agent's prompt (never the retrieval lottery), which is the structural fix
//     for "the address was in the KB but the bot said it didn't know."
//
// Lives apart from Ajustes (which keeps the operational settings) so neither is
// cluttered. Loads + saves through the same owner `settings` capability, editing
// only the identityProfile slice.

import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  composeIdentity,
  isIdentityProfileComplete,
  type BusinessField,
  type IdentityProfile,
} from '@vysto/shared/bot-config/identity'
import { useState } from 'react'

import type { FlowGateway } from './flows-data.js'
import {
  Button,
  FormField,
  IconButton,
  Input,
  Modal,
  Select,
  Spinner,
  Textarea,
  toast,
} from '../../design-system/index.js'

const BLANK: IdentityProfile = { businessName: '', activity: '', treatment: 'tu' }

/** Vertical-specific facts we prompt for (label set per industry). The owner
 *  fills the values; the labels are stored with them so the engine renders them
 *  without knowing the vertical. */
const VERTICAL_FIELDS: Record<string, string[]> = {
  'automotive-new': ['Marcas y modelos', 'Promociones o financiamiento'],
  'automotive-used': ['Autos disponibles', 'Garantía o financiamiento'],
  healthcare: ['Especialidades', 'Médicos', 'Seguros que aceptan'],
  'casas-de-empeno': ['Qué artículos aceptan', 'Tasas o condiciones'],
}

/** The vertical fields to show. Once the owner has saved a set (`saved` is
 *  defined — even `[]`), respect it EXACTLY: a field they removed or left empty
 *  (save drops empties) stays gone, instead of the suggestion re-appearing every
 *  time. The vertical's suggested labels only seed a FRESH profile that has never
 *  saved vertical fields. */
function seedVerticalFields(
  vertical: string | null,
  saved: BusinessField[] | undefined,
): BusinessField[] {
  if (saved !== undefined) return saved
  const suggested = vertical ? (VERTICAL_FIELDS[vertical] ?? []) : []
  return suggested.map((label) => ({ label, value: '' }))
}

export function BusinessProfileModal({
  settings,
  vertical,
  onClose,
}: {
  settings: NonNullable<FlowGateway['settings']>
  vertical: string | null
  onClose: () => void
}): JSX.Element {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['flow-editor', 'agent-settings'],
    queryFn: () => settings.load(),
  })
  const [profile, setProfile] = useState<IdentityProfile | null>(null)
  const [fields, setFields] = useState<BusinessField[] | null>(null)
  const [busy, setBusy] = useState(false)

  // Initialize local state once the settings load.
  const base = profile ?? data?.identityProfile ?? (data ? BLANK : null)
  const rows =
    fields ?? (data ? seedVerticalFields(vertical, data.identityProfile?.verticalFields) : [])

  const set = (patch: Partial<IdentityProfile>): void => {
    if (base) setProfile({ ...base, ...patch })
  }
  const setRow = (i: number, patch: Partial<BusinessField>): void =>
    setFields(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)))
  const addRow = (): void => setFields([...rows, { label: '', value: '' }])
  const removeRow = (i: number): void => setFields(rows.filter((_, j) => j !== i))

  async function save(): Promise<void> {
    if (!base || !data) return
    if (!isIdentityProfileComplete(base)) {
      toast.error('Falta el nombre del negocio y a qué se dedica.')
      return
    }
    setBusy(true)
    try {
      const verticalFields = rows
        .map((r) => ({ label: r.label.trim(), value: r.value.trim() }))
        .filter((r) => r.label !== '' && r.value !== '')
      await settings.save({
        ...data,
        identityProfile: { ...base, verticalFields },
      })
      // Drop the stale cache so reopening shows the saved values (incl. cleared
      // fields) instead of the pre-edit snapshot (staleTime is 10s).
      await qc.invalidateQueries({ queryKey: ['flow-editor', 'agent-settings'] })
      toast.ok('Perfil guardado.')
      onClose()
    } catch {
      toast.error('No se pudo guardar el perfil.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Perfil del negocio"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button onClick={() => void save()} loading={busy} disabled={!base}>
            Guardar perfil
          </Button>
        </>
      }
    >
      {isLoading || !base ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-5">
          <p className="text-[12px] leading-relaxed text-text-2">
            Lo que escribas aquí el agente <strong>siempre lo sabe</strong> (no depende de los
            documentos). Mientras más completo, mejor responde. Para catálogos largos o detalles,
            usa <strong>Conocimiento</strong>.
          </p>

          {/* Identity */}
          <div className="space-y-3">
            <p className="text-[12px] font-semibold text-text">Tu negocio</p>
            <FormField label="¿Cómo se llama tu negocio?" htmlFor="bp-business">
              <Input
                id="bp-business"
                value={base.businessName}
                onChange={(e) => set({ businessName: e.target.value })}
                placeholder="p. ej. Hospital Vitalia"
              />
            </FormField>
            <FormField label="¿A qué se dedica?" htmlFor="bp-activity">
              <Input
                id="bp-activity"
                value={base.activity}
                onChange={(e) => set({ activity: e.target.value })}
                placeholder="p. ej. un hospital privado"
              />
            </FormField>
            <FormField label="¿Cómo se llama el asistente? (opcional)" htmlFor="bp-agent">
              <Input
                id="bp-agent"
                value={base.agentName ?? ''}
                onChange={(e) => set({ agentName: e.target.value })}
                placeholder="p. ej. Vera"
              />
            </FormField>
            <FormField label="¿Cómo se dirige al cliente?" htmlFor="bp-treatment">
              <Select
                id="bp-treatment"
                value={base.treatment}
                onChange={(e) => set({ treatment: e.target.value as 'tu' | 'usted' })}
                options={[
                  { value: 'tu', label: 'De tú (cercano)' },
                  { value: 'usted', label: 'De usted (formal)' },
                ]}
              />
            </FormField>
            <div className="rounded-md bg-surface-2 px-3 py-2">
              <p className="text-[11px] font-semibold text-text-2">Así se presentará:</p>
              <p className="mt-1 text-[12px] leading-relaxed text-text">
                {isIdentityProfileComplete(base)
                  ? composeIdentity(base)
                  : 'Completa el nombre del negocio y a qué se dedica para ver la vista previa.'}
              </p>
            </div>
          </div>

          {/* Essentials — always known */}
          <div className="space-y-3 border-t border-line pt-4">
            <p className="text-[12px] font-semibold text-text">Datos que el agente siempre sabe</p>
            <FormField label="Dirección / ubicación" htmlFor="bp-address">
              <Input
                id="bp-address"
                value={base.address ?? ''}
                onChange={(e) => set({ address: e.target.value })}
                placeholder="p. ej. Av. Lázaro Cárdenas 2400, San Pedro"
              />
            </FormField>
            <FormField label="Horario de atención" htmlFor="bp-hours">
              <Input
                id="bp-hours"
                value={base.hours ?? ''}
                onChange={(e) => set({ hours: e.target.value })}
                placeholder="p. ej. Lunes a viernes de 8:00 a 20:00"
              />
            </FormField>
            <FormField label="Teléfono o forma de contacto" htmlFor="bp-phone">
              <Input
                id="bp-phone"
                value={base.phone ?? ''}
                onChange={(e) => set({ phone: e.target.value })}
                placeholder="p. ej. 81 2155 9000"
              />
            </FormField>
            <FormField label="Servicios o productos principales" htmlFor="bp-services">
              <Textarea
                id="bp-services"
                value={base.services ?? ''}
                onChange={(e) => set({ services: e.target.value })}
                placeholder="p. ej. Cardiología, Pediatría, Urgencias 24h, Laboratorio"
              />
            </FormField>
            <FormField label="¿Cómo se agenda o se compra?" htmlFor="bp-booking">
              <Input
                id="bp-booking"
                value={base.booking ?? ''}
                onChange={(e) => set({ booking: e.target.value })}
                placeholder="p. ej. Por WhatsApp al 81 2155 9010 o por teléfono"
              />
            </FormField>
            <FormField
              label="Aseguradoras que aceptan"
              htmlFor="bp-insurers"
              helper="Separadas por comas. El agente responde con esto a “¿aceptan mi seguro?”."
            >
              <Input
                id="bp-insurers"
                value={(base.acceptedInsurers ?? []).join(', ')}
                onChange={(e) =>
                  set({
                    acceptedInsurers: e.target.value
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
                placeholder="p. ej. GNP, AXA, MetLife, Mapfre"
              />
            </FormField>
          </div>

          {/* Vertical-specific + custom */}
          <div className="space-y-3 border-t border-line pt-4">
            <p className="text-[12px] font-semibold text-text">
              Datos específicos de tu giro
              <span className="ml-1 font-normal text-text-3">(opcional)</span>
            </p>
            {rows.map((row, i) => (
              <div key={i} className="flex items-end gap-2">
                <FormField
                  label={i === 0 ? 'Dato' : ''}
                  htmlFor={`bp-vf-label-${i}`}
                  className="w-2/5"
                >
                  <Input
                    id={`bp-vf-label-${i}`}
                    value={row.label}
                    onChange={(e) => setRow(i, { label: e.target.value })}
                    placeholder="p. ej. Especialidades"
                  />
                </FormField>
                <FormField
                  label={i === 0 ? 'Información' : ''}
                  htmlFor={`bp-vf-value-${i}`}
                  className="flex-1"
                >
                  <Input
                    id={`bp-vf-value-${i}`}
                    value={row.value}
                    onChange={(e) => setRow(i, { value: e.target.value })}
                    placeholder="p. ej. Cardiología (Dra. Robles, 304), Pediatría…"
                  />
                </FormField>
                <IconButton
                  aria-label="Quitar dato"
                  variant="ghost"
                  onClick={() => removeRow(i)}
                  className="mb-1"
                  icon={
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      aria-hidden
                    >
                      <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
                    </svg>
                  }
                />
              </div>
            ))}
            <Button variant="ghost" size="sm" onClick={addRow}>
              + Agregar dato
            </Button>
          </div>

          {/* Free-form */}
          <div className="space-y-3 border-t border-line pt-4">
            <FormField
              label="Más sobre tu negocio (opcional)"
              htmlFor="bp-extra"
              helper="Cualquier detalle adicional que quieras que el agente tenga siempre presente."
            >
              <Textarea
                id="bp-extra"
                value={base.extra ?? ''}
                onChange={(e) => set({ extra: e.target.value })}
                placeholder="p. ej. Contamos con estacionamiento gratis y atención sin cita en urgencias."
              />
            </FormField>
          </div>
        </div>
      )}
    </Modal>
  )
}
